import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import SideDrawer from "../../common/side-drawer";
import ButtonOne from "../../common/normal-button";
import AppDatePicker from "../../common/app-date-picker";
import TriggerRowsEditor from "./Components/TriggerRowsEditor";
import ActiveWindowEditor from "./Components/ActiveWindowEditor";
import AudienceFilterEditor from "./Components/AudienceFilterEditor";
import StatusSection from "./Components/StatusSection";
import {
  MAX_TRIGGERS,
  labelSx,
  sectionLabelSx,
  emptyTextRow,
  mapApiTriggersToRows,
  buildTriggerPayload,
  validateTriggerRows,
  fetchApprovedTemplateOptions,
  triggersFingerprint,
} from "./utils";
import { ColorsConstants, borderColors } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import { NODEBOT_NODEBOTS, NODEBOT_NODEBOTS_DETAIL } from "../../config/urls";

const WorkflowEdit = (props) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const workflowId =
    props.pageParams?.workflowId || props.pageParams?.workflow?.id;

  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [workflowName, setWorkflowName] = useState(
    props.pageParams?.workflowName ||
      props.pageParams?.workflow?.workflowName ||
      ""
  );

  const [isActive, setIsActive] = useState(false);
  // Seeded from the row that linked here (if available) so the page can show the
  // correct "Chatbot"/"Automation" naming immediately, before the detail fetch resolves.
  const [triggerType, setTriggerType] = useState(
    props.pageParams?.triggerType || props.pageParams?.workflow?.triggerType || "message"
  );
  const entityLabel = triggerType === "recurring" ? "Automation" : "Chatbot";

  // One row per trigger — mixing "text" and "template_button_reply" rows is allowed.
  const [triggers, setTriggers] = useState([]);
  const nextKeyRef = useRef(0);
  const nextKey = () => ++nextKeyRef.current;

  const [recurringStartDate, setRecurringStartDate] = useState(null);
  const [recurringEndDate, setRecurringEndDate] = useState(null);
  const [useDateConstraints, setUseDateConstraints] = useState(false);
  const [activeStartDate, setActiveStartDate] = useState(null);
  const [activeEndDate, setActiveEndDate] = useState(null);

  // Shared template list for every "template_button_reply" row's picker
  const [templateOptions, setTemplateOptions] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Snapshot of the editable fields as last loaded from the server — compared against
  // current state to report `onDirtyChange`. Kept as a ref (not state) since it's only
  // ever read inside the dirty-check effect below, never rendered.
  const initialSnapshotRef = useRef(null);

  // Re-fetches every time the drawer opens (not just when the id changes) so reopening
  // the same workflow after an abandoned, unsaved edit shows fresh server data instead
  // of the stale in-memory draft.
  useEffect(() => {
    if (!props.open) return;
    if (!workflowId) {
      setFetchError(`No ${entityLabel.toLowerCase()} ID provided.`);
      setFetchLoading(false);
      return;
    }
    const load = async () => {
      setFetchLoading(true);
      setFetchError("");
      try {
        const token = await getValidToken();
        const response = await fetch(
          `${NODEBOT_NODEBOTS_DETAIL}${workflowId}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              "X-Tenant-Id": tenant,
            },
          }
        );
        if (!response.ok) throw new Error(`Failed to load ${entityLabel.toLowerCase()}`);
        const bot = await response.json();
        setWorkflowName(bot.name || "Untitled Workflow");
        setIsActive(bot.is_active ?? false);
        setTriggerType(bot.trigger_type || "message");
        const triggerRows = mapApiTriggersToRows(bot.triggers, nextKey);
        setTriggers(triggerRows);
        setRecurringStartDate(
          bot.recurring_start_date ? dayjs(bot.recurring_start_date) : null
        );
        setRecurringEndDate(
          bot.recurring_end_date ? dayjs(bot.recurring_end_date) : null
        );
        setUseDateConstraints(bot.use_date_constraints ?? false);
        setActiveStartDate(bot.active_start_date ? dayjs(bot.active_start_date) : null);
        setActiveEndDate(bot.active_end_date ? dayjs(bot.active_end_date) : null);
        initialSnapshotRef.current = JSON.stringify({
          isActive: bot.is_active ?? false,
          triggers: triggersFingerprint(triggerRows),
          recurringStartDate: bot.recurring_start_date || null,
          recurringEndDate: bot.recurring_end_date || null,
          useDateConstraints: bot.use_date_constraints ?? false,
          activeStartDate: bot.active_start_date || null,
          activeEndDate: bot.active_end_date || null,
        });
      } catch (err) {
        setFetchError(err.message || `Failed to load ${entityLabel.toLowerCase()}`);
      } finally {
        setFetchLoading(false);
      }
    };
    load();
  }, [workflowId, props.open]);

  // Reports unsaved-changes state up to the Workflows list so it can warn before
  // switching tabs (which would otherwise silently discard this drawer's edits).
  useEffect(() => {
    if (!props.onDirtyChange) return;
    if (!props.open || fetchLoading || !initialSnapshotRef.current) {
      props.onDirtyChange(false);
      return;
    }
    const current = JSON.stringify({
      isActive,
      triggers: triggersFingerprint(triggers),
      recurringStartDate: recurringStartDate ? recurringStartDate.format("YYYY-MM-DD") : null,
      recurringEndDate: recurringEndDate ? recurringEndDate.format("YYYY-MM-DD") : null,
      useDateConstraints,
      activeStartDate: activeStartDate ? activeStartDate.format("YYYY-MM-DD") : null,
      activeEndDate: activeEndDate ? activeEndDate.format("YYYY-MM-DD") : null,
    });
    props.onDirtyChange(current !== initialSnapshotRef.current);
  }, [
    props.open,
    fetchLoading,
    isActive,
    triggers,
    recurringStartDate,
    recurringEndDate,
    useDateConstraints,
    activeStartDate,
    activeEndDate,
  ]);

  // Fetch approved templates for the Template Button Reply pickers
  const fetchTemplateOptions = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      setTemplateOptions(await fetchApprovedTemplateOptions(getValidToken, tenant));
    } catch (err) {
      console.error("Error fetching templates:", err.message);
    } finally {
      setTemplatesLoading(false);
    }
  }, [getValidToken, tenant]);

  useEffect(() => {
    fetchTemplateOptions();
  }, [fetchTemplateOptions]);

  // Resolve each row's saved template/button once both the template list AND the
  // workflow's own triggers are loaded. Depends on `fetchLoading` too (not just
  // `templateOptions`) because the two fetches race independently — if templates
  // resolve first, this ran once already with `triggers` still empty and would
  // otherwise never fire again once `load()` later populates the pending rows.
  useEffect(() => {
    if (templateOptions.length === 0 || fetchLoading) return;
    setTriggers((prev) =>
      prev.map((row) => {
        if (row.type !== "template_button_reply" || row.selectedTemplate || !row.pendingTemplateId) {
          return row;
        }
        const template = templateOptions.find((t) => t.id === row.pendingTemplateId);
        if (!template) return row;
        // Button ids come back as a JS number from the template's allButtons JSON but
        // as a string from the trigger's button_id (CharField) — compare as strings so
        // a saved trigger's button still resolves instead of silently staying null.
        const button =
          template.buttons.find((b) => String(b.id) === String(row.pendingButtonId)) || null;
        return { ...row, selectedTemplate: template, selectedButton: button };
      })
    );
  }, [templateOptions, fetchLoading]);

  const handleClose = () => {
    props.onClose && props.onClose();
  };

  const addTriggerRow = () => {
    setTriggers((prev) => (prev.length >= MAX_TRIGGERS ? prev : [...prev, emptyTextRow(nextKey())]));
  };

  const removeTriggerRow = (key) => {
    setTriggers((prev) => prev.filter((row) => row.key !== key));
  };

  const updateTriggerRow = (key, patch) => {
    setTriggers((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    setErrors((prev) => ({ ...prev, triggers: undefined }));
  };

  const validate = () => {
    const errs = {};

    if (triggerType === "message") {
      const triggersError = validateTriggerRows(triggers, "nodebot");
      if (triggersError) errs.triggers = triggersError;
    }

    if (triggerType === "recurring") {
      if (!recurringStartDate) errs.recurringStartDate = "Start date is required";
      if (!recurringEndDate) errs.recurringEndDate = "End date is required";
      if (
        recurringStartDate &&
        recurringEndDate &&
        recurringEndDate.isBefore(recurringStartDate)
      )
        errs.recurringEndDate = "End date must be after start date";
    }
    if (triggerType !== "recurring" && useDateConstraints) {
      if (!activeStartDate) errs.activeStartDate = "Start date is required";
      if (!activeEndDate) errs.activeEndDate = "End date is required";
      if (activeStartDate && activeEndDate && activeEndDate.isBefore(activeStartDate))
        errs.activeEndDate = "End date must be after start date";
    }
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSaving(true);

    try {
      const token = await getValidToken();
      const payload = {
        is_active: isActive,
        trigger_type: triggerType,
        triggers: triggerType === "message" ? buildTriggerPayload(triggers) : [],
      };
      if (triggerType === "recurring") {
        payload.recurring_start_date = recurringStartDate
          ? recurringStartDate.format("YYYY-MM-DD")
          : null;
        payload.recurring_end_date = recurringEndDate
          ? recurringEndDate.format("YYYY-MM-DD")
          : null;
      } else {
        payload.use_date_constraints = useDateConstraints;
        payload.active_start_date = useDateConstraints && activeStartDate
          ? activeStartDate.format("YYYY-MM-DD")
          : null;
        payload.active_end_date = useDateConstraints && activeEndDate
          ? activeEndDate.format("YYYY-MM-DD")
          : null;
      }

      const response = await fetch(`${NODEBOT_NODEBOTS}${workflowId}/`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const message = data?.error || `Failed to save ${entityLabel.toLowerCase()}`;
        // The backend rejects a keyword already claimed by another bot (or a
        // same-request duplicate) with a "Trigger ..." message — surface that inline,
        // next to the trigger it's about, instead of as a generic toast.
        if (/^trigger\b/i.test(message)) {
          setErrors((prev) => ({ ...prev, triggers: message }));
          return;
        }
        throw new Error(message);
      }

      const saved = await response.json().catch(() => ({}));
      const warnings = saved?.warnings || [];
      // Not a save failure — the backend allows this combination, just flags it (e.g.
      // this bot's "Any Message" trigger now shadows the active Fallback Autoresponder).
      setSnackbar({
        open: true,
        message: warnings.length
          ? `${entityLabel} saved. ${warnings.join(" ")}`
          : `${entityLabel} saved successfully`,
        severity: warnings.length ? "warning" : "success",
      });
      props.onSaved && props.onSaved();
      setTimeout(() => handleClose(), 1000);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || `Failed to save ${entityLabel.toLowerCase()}`,
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SideDrawer
      open={!!props.open}
      onClose={handleClose}
      title={`Edit ${entityLabel}`}
      subtitle={workflowName}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {fetchLoading ? (
          <Box
            sx={{
              height: "50vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography
              sx={{ ...typography.bodyLarge, color: ColorsConstants.primaryGray40 }}
            >
              Loading {entityLabel.toLowerCase()}...
            </Typography>
          </Box>
        ) : (
          <>
            {fetchError && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: "8px" }}>
                {fetchError}
              </Alert>
            )}

            {/* Form Card — full width */}
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                border: "1px solid #E5E7EB",
                p: 3,
              }}
            >
              {/* Section: Status */}
              <StatusSection
                isActive={isActive}
                onIsActiveChange={setIsActive}
                group={triggerType === "recurring" ? "Automation" : "Configuration"}
              />

              <Divider style={{marginBlock: "1em"}} />
              {/* Section: Trigger Configuration */}
              <Typography sx={sectionLabelSx}>Trigger Configuration</Typography>

              {/* Triggers list — mixing text keyword and template button reply rows */}
              {triggerType === "message" && (
                <>
                  <Box sx={{ mb: 3 }}>
                    <TriggerRowsEditor
                      triggers={triggers}
                      templateOptions={templateOptions}
                      templatesLoading={templatesLoading}
                      errorMessage={errors.triggers}
                      onAddRow={addTriggerRow}
                      onRemoveRow={removeTriggerRow}
                      onUpdateRow={updateTriggerRow}
                    />
                  </Box>
                  <AudienceFilterEditor />
                </>
              )}

              {/* Recurring Dates (only for recurring) */}
              {triggerType === "recurring" && (
                <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={labelSx}>Start Date</Typography>
                    <AppDatePicker
                      value={recurringStartDate}
                      onChange={(val) => {
                        setRecurringStartDate(val);
                        setErrors((prev) => ({
                          ...prev,
                          recurringStartDate: undefined,
                        }));
                      }}
                      error={!!errors.recurringStartDate}
                      helperText={errors.recurringStartDate}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={labelSx}>End Date</Typography>
                    <AppDatePicker
                      value={recurringEndDate}
                      onChange={(val) => {
                        setRecurringEndDate(val);
                        setErrors((prev) => ({
                          ...prev,
                          recurringEndDate: undefined,
                        }));
                      }}
                      minDate={recurringStartDate || undefined}
                      error={!!errors.recurringEndDate}
                      helperText={errors.recurringEndDate}
                    />
                  </Box>
                </Box>
              )}

              {/* Date Constraints (message / recurring — recurring uses its own dates instead) */}
              {triggerType !== "recurring" && (
                <ActiveWindowEditor
                  useDateConstraints={useDateConstraints}
                  activeStartDate={activeStartDate}
                  activeEndDate={activeEndDate}
                  errors={errors}
                  onUseDateConstraintsChange={(limited) => {
                    setUseDateConstraints(limited);
                    if (!limited) {
                      setActiveStartDate(null);
                      setActiveEndDate(null);
                      setErrors((prev) => ({ ...prev, activeStartDate: undefined, activeEndDate: undefined }));
                    }
                  }}
                  onActiveStartDateChange={(val) => {
                    setActiveStartDate(val);
                    setErrors((prev) => ({ ...prev, activeStartDate: undefined }));
                  }}
                  onActiveEndDateChange={(val) => {
                    setActiveEndDate(val);
                    setErrors((prev) => ({ ...prev, activeEndDate: undefined }));
                  }}
                />
              )}

              {/* Action Buttons */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
                <ButtonOne
                  variant="outlined"
                  onClick={handleClose}
                  disabled={saving}
                  sx={{
                    height: 40,
                    px: 2.5,
                    ...typography.bodyLarge,
                    border: `1px solid ${borderColors.inputFieldGrey}`,
                    color: "#1D2330",
                    "&:hover": {
                      border: `1px solid ${ColorsConstants.primaryGray50}`,
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  Cancel
                </ButtonOne>
                <ButtonOne
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || !!fetchError}
                  startIcon={
                    saving ? (
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                    ) : null
                  }
                  sx={{
                    height: 40,
                    px: 2.5,
                    ...typography.bodyLarge,
                    backgroundColor: ColorsConstants.primaryBackgroundColor,
                    "&:hover": {
                      backgroundColor: ColorsConstants.primaryBackgroundColor,
                      opacity: 0.9,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: ColorsConstants.primaryBackgroundColor,
                      opacity: 0.6,
                    },
                  }}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </ButtonOne>
              </Box>
            </Box>

            <Snackbar
              open={snackbar.open}
              autoHideDuration={3000}
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              sx={{ bottom: { xs: 16, sm: 24 }, right: { xs: 16, sm: 24 } }}
            >
              <Alert
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                severity={snackbar.severity}
                sx={{ width: "100%" }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          </>
        )}
      </LocalizationProvider>
    </SideDrawer>
  );
};

export default WorkflowEdit;
